import { ReactNode, ReactElement, useEffect } from "react"
import { connect } from "react-redux"
import { MainThunkDispatch, State } from "../../../Reducers"
import { ThunkAction } from "redux-thunk"
import { Action } from "redux"

export interface ProviderProps<T> {
    render: (data?: T) => ReactNode
}

interface StateProps<T> {
    data?: T
    shouldFetch: boolean
}

interface DispatchProps {
    fetch: () => void
}

type Props<T> = ProviderProps<T> & StateProps<T> & DispatchProps

function ProviderIsolated<T>({
    data,
    render,
    fetch,
    shouldFetch
}: Props<T>): ReactNode {
    useEffect(() => {
        if (shouldFetch) {
            fetch()
        }
    }, [shouldFetch, fetch])

    return render(data)
}

type DataSelector<T, P> = (state: State, ownProps: P) => T | undefined

type ForceFetchSelector<P> = (state: State, ownProps: P) => boolean

type Fetcher<P> = (ownProps: P) => ThunkAction<void, State, any, Action>

export function createProvider<T, P extends {}>(
    /**
     * Function to select data. If there is none, it should return undefined.
     */
    selectData: DataSelector<T, P>,
    /**
     * Redux Action creator to fetch data.
     */
    fetchAction: Fetcher<P>,
    /**
     * Optional function to decide if fetch should be triggered.
     * Useful when you want to have more control over when data should be fetched.
     * By default, fetch is triggered when the returning value of `selectData(...)` is `undefined`.
     */
    selectShouldFetch?: ForceFetchSelector<P>
) {
    type OwnProps = ProviderProps<T> & P

    function mapStateToProps(state: State, ownProps: P): StateProps<T> {
        const data = selectData(state, ownProps)

        return {
            data,
            shouldFetch:
                selectShouldFetch === undefined
                    ? data === undefined
                    : selectShouldFetch(state, ownProps)
        }
    }

    function mapDispatchToProps(
        dispatch: MainThunkDispatch,
        ownProps: P
    ): DispatchProps {
        return {
            fetch: () => dispatch(fetchAction(ownProps))
        }
    }

    return connect<StateProps<T>, DispatchProps, OwnProps, State>(
        mapStateToProps,
        mapDispatchToProps
    )(ProviderIsolated as (props: Props<T>) => ReactElement)
}
